import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Stack,
  Avatar,
  Card,
  Divider,
  Link,
  Grid,
  Collapse,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Autocomplete,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import HistoryIcon from '@mui/icons-material/History';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SyncIcon from '@mui/icons-material/Sync';
import GitHubIcon from '@mui/icons-material/GitHub';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import ReplayIcon from '@mui/icons-material/Replay';
import DescriptionIcon from '@mui/icons-material/Description';
import TerminalIcon from '@mui/icons-material/Terminal';
import {
  fetchDeployments,
  triggerDeploy,
  triggerRollback,
  fetchDeploymentHistory,
  fetchActiveIncidents,
  createIncident,
  updateIncident,
  fetchWorkflowRuns,
  fetchWorkflowJobs,
  fetchWorkflowLogs,
  fetchCommits,
  fetchBranches,
  fetchChangelog,
  syncDeploymentHistory,
  syncAllDeployments,
  DeploymentInfo,
  DeploymentHistoryItem,
  WorkflowRun,
  WorkflowJob,
  WorkflowLogsResponse,
  GitHubCommit,
  GitHubBranch,
  Changelog,
} from '@/services/opsApi';
import { fetchStatus, StatusData } from '@/services/statusApi';
import { useAuth } from '@/contexts/AuthContext';

const STATUS_COLORS = {
  operational: 'success',
  degraded: 'warning',
  outage: 'error',
  unknown: 'default',
  triggered: 'info',
  pending: 'default',
  in_progress: 'warning',
  success: 'success',
  failure: 'error',
  cancelled: 'default',
  failed_to_trigger: 'error',
  investigating: 'error',
  identified: 'warning',
  monitoring: 'info',
  resolved: 'success',
  minor: 'warning',
  major: 'error',
  critical: 'error',
  queued: 'default',
  completed: 'success',
} as const;

const SEVERITY_OPTIONS = [
  { value: 'minor', label: 'Minor' },
  { value: 'major', label: 'Major' },
  { value: 'critical', label: 'Critical' },
];

const STATUS_OPTIONS = [
  { value: 'investigating', label: 'Investigating' },
  { value: 'identified', label: 'Identified' },
  { value: 'monitoring', label: 'Monitoring' },
  { value: 'resolved', label: 'Resolved' },
];

interface Incident {
  incident_id: string;
  title: string;
  status: string;
  severity: string;
  created_at: string;
  updated_at?: string;
  affected_components: string[];
}

function StatusChip({ status, size = 'small' }: { status: string; size?: 'small' | 'medium' }) {
  const color = STATUS_COLORS[status as keyof typeof STATUS_COLORS] || 'default';
  const label = status === 'in_progress'
    ? 'IN PROGRESS'
    : status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  return (
    <Chip
      label={label}
      color={color as 'success' | 'warning' | 'error' | 'default' | 'info'}
      size={size}
    />
  );
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins < 60) return `${mins}m ${secs}s`;
  const hours = Math.floor(mins / 60);
  return `${hours}h ${mins % 60}m`;
}

// Active Deployments Banner
function ActiveDeploymentsBanner({
  runs,
  onViewLogs,
}: {
  runs: WorkflowRun[];
  onViewLogs: (run: WorkflowRun) => void;
}) {
  const activeRuns = runs.filter((r) => r.status === 'in_progress' || r.status === 'queued');

  if (activeRuns.length === 0) return null;

  return (
    <Alert
      severity="info"
      icon={<PlayArrowIcon />}
      sx={{ mb: { xs: 2, md: 3 }, '& .MuiAlert-message': { width: '100%' } }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
        <Box flex={1}>
          <Typography fontWeight={600} fontSize={{ xs: '0.875rem', md: '1rem' }}>
            {activeRuns.length} deployment{activeRuns.length > 1 ? 's' : ''} in progress
          </Typography>
          <Stack direction="row" spacing={{ xs: 1, md: 2 }} mt={0.5} flexWrap="wrap" gap={{ xs: 0.5, md: 0 }}>
            {activeRuns.map((run) => (
              <Box key={run.id} display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                <CircularProgress size={12} />
                <Typography variant="body2" fontSize={{ xs: '0.75rem', md: '0.875rem' }}>
                  {run.deployment_id || run.name}: {run.head_sha?.substring(0, 7) || 'N/A'}
                  {run.triggering_actor?.login && ` by ${run.triggering_actor.login}`}
                </Typography>
                <Tooltip title="View Logs">
                  <IconButton size="small" onClick={() => onViewLogs(run)}>
                    <TerminalIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>
    </Alert>
  );
}

// Recent Commits List
function RecentCommitsList({
  commits,
  deployments,
}: {
  commits: GitHubCommit[];
  deployments: DeploymentInfo[];
}) {
  const [expanded, setExpanded] = useState(true);

  // Create a map of deployed commits
  const deployedCommits = new Map<string, string[]>();
  deployments.forEach((d) => {
    if (d.commit_sha) {
      const existing = deployedCommits.get(d.commit_sha) || [];
      existing.push(d.label || d.deployment_id);
      deployedCommits.set(d.commit_sha, existing);
    }
  });

  return (
    <Card sx={{ mb: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          p: { xs: 1.5, md: 2 },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <GitHubIcon color="primary" fontSize={window.innerWidth < 600 ? 'small' : 'medium'} />
          <Typography variant="h6" fontSize={{ xs: '1rem', md: '1.25rem' }}>Recent Commits</Typography>
          <Chip label={commits.length} size="small" />
        </Box>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Divider />
        <List sx={{ py: 0 }}>
          {commits.slice(0, 10).map((commit, index) => {
            const isDeployed = deployedCommits.has(commit.sha);
            const deployedTo = deployedCommits.get(commit.sha) || [];

            return (
              <ListItem
                key={commit.sha}
                divider={index < commits.length - 1}
                sx={{
                  bgcolor: isDeployed ? 'success.50' : undefined,
                  '&:hover': { bgcolor: isDeployed ? 'success.100' : 'action.hover' },
                  px: { xs: 1, md: 2 },
                  py: { xs: 1, md: 2 },
                }}
              >
                <ListItemAvatar sx={{ minWidth: { xs: 40, md: 56 } }}>
                  <Avatar
                    src={commit.author.avatar_url}
                    alt={commit.author.name}
                    sx={{ width: { xs: 28, md: 32 }, height: { xs: 28, md: 32 } }}
                  >
                    {commit.author.name?.[0] || '?'}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Link
                        href={commit.html_url}
                        target="_blank"
                        rel="noopener"
                        underline="hover"
                        color="inherit"
                        sx={{ fontWeight: 500, fontSize: { xs: '0.875rem', md: '1rem' } }}
                      >
                        {commit.message}
                      </Link>
                      {isDeployed && (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {deployedTo.map((region) => (
                            <Chip
                              key={region}
                              label={region}
                              size="small"
                              color="success"
                              variant="outlined"
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                          ))}
                        </Stack>
                      )}
                    </Box>
                  }
                  secondary={
                    <Box display="flex" gap={{ xs: 1, md: 2 }} alignItems="center" flexWrap="wrap">
                      <Typography variant="caption" fontFamily="monospace" fontSize={{ xs: '0.7rem', md: '0.75rem' }}>
                        {commit.short_sha}
                      </Typography>
                      <Typography variant="caption" fontSize={{ xs: '0.7rem', md: '0.75rem' }}>
                        {commit.author.login || commit.author.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontSize={{ xs: '0.7rem', md: '0.75rem' }}>
                        {formatRelativeTime(commit.author.date)}
                      </Typography>
                    </Box>
                  }
                />
                <Tooltip title="Open in GitHub">
                  <IconButton
                    size="small"
                    href={commit.html_url}
                    target="_blank"
                    rel="noopener"
                    sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </ListItem>
            );
          })}
        </List>
      </Collapse>
    </Card>
  );
}

// Enhanced Deployment Table with Expandable Rows
function EnhancedDeploymentTable({
  deployments,
  workflowRuns,
  statusData,
  onDeploy,
  onViewHistory,
  onSync,
  onRollback,
  onChangelog,
}: {
  deployments: DeploymentInfo[];
  workflowRuns: WorkflowRun[];
  statusData: StatusData | null;
  onDeploy: (deployment: DeploymentInfo) => void;
  onViewHistory: (deployment: DeploymentInfo) => void;
  onSync: (deployment: DeploymentInfo) => void;
  onRollback: (deployment: DeploymentInfo) => void;
  onChangelog: (deployment: DeploymentInfo) => void;
}) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (deploymentId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(deploymentId)) {
      newExpanded.delete(deploymentId);
    } else {
      newExpanded.add(deploymentId);
    }
    setExpandedRows(newExpanded);
  };

  // Map workflow runs to deployments
  const deploymentRuns = new Map<string, WorkflowRun[]>();
  workflowRuns.forEach((run) => {
    if (run.deployment_id) {
      const runs = deploymentRuns.get(run.deployment_id) || [];
      runs.push(run);
      deploymentRuns.set(run.deployment_id, runs);
    }
  });

  // Map status data by deployment
  const statusByDeployment = new Map<string, StatusData['deployments'][0]>();
  statusData?.deployments?.forEach((d) => {
    statusByDeployment.set(d.deployment_id, d);
  });

  return (
    <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
      <Table sx={{ minWidth: { xs: 800, md: 'auto' } }}>
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell sx={{ width: 40 }} />
            <TableCell sx={{ fontWeight: 600, minWidth: { xs: 150, md: 'auto' } }}>Deployment</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: { xs: 120, md: 'auto' } }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: { xs: 140, md: 'auto' } }}>Current Version</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: { xs: 180, md: 'auto' } }}>Last Deployment</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, minWidth: { xs: 150, md: 'auto' } }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {deployments.map((deployment) => {
            const runs = deploymentRuns.get(deployment.deployment_id) || [];
            const latestRun = runs[0];
            const isDeploying = latestRun?.status === 'in_progress' || latestRun?.status === 'queued';
            const isExpanded = expandedRows.has(deployment.deployment_id);
            const statusInfo = statusByDeployment.get(deployment.deployment_id);

            return (
              <>
                <TableRow
                  key={deployment.deployment_id}
                  sx={{
                    '&:hover': { bgcolor: 'action.hover' },
                    bgcolor: isDeploying ? 'info.50' : undefined,
                    cursor: 'pointer',
                  }}
                  onClick={() => toggleRow(deployment.deployment_id)}
                >
                  <TableCell>
                    <IconButton size="small">
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography fontWeight={600}>
                        {deployment.label || deployment.deployment_id}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          {deployment.region}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          •
                        </Typography>
                        <Link
                          href={`https://${deployment.domain}`}
                          target="_blank"
                          rel="noopener"
                          variant="caption"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {deployment.domain}
                        </Link>
                      </Stack>
                      <Chip
                        label={deployment.environment}
                        size="small"
                        variant="outlined"
                        sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <StatusChip status={deployment.status} />
                      {isDeploying && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <CircularProgress size={14} />
                          <Typography variant="caption" color="info.main">
                            Deploying...
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {deployment.commit_sha ? (
                      <Link
                        href={`https://github.com/Roboticks-IO/roboticks/commit/${deployment.commit_sha}`}
                        target="_blank"
                        rel="noopener"
                        sx={{ fontFamily: 'monospace' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {deployment.commit_sha.substring(0, 7)}
                      </Link>
                    ) : (
                      <Typography color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {/* Show in-progress run if available, otherwise show last_deployment from API */}
                    {isDeploying && latestRun ? (
                      <Box>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <StatusChip status={latestRun.status} size="small" />
                          {latestRun.html_url && (
                            <Tooltip title="View in GitHub Actions">
                              <IconButton
                                size="small"
                                href={latestRun.html_url}
                                target="_blank"
                                rel="noopener"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <GitHubIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {formatRelativeTime(latestRun.created_at)}
                          {latestRun.triggering_actor?.login && (
                            <> by {latestRun.triggering_actor.login}</>
                          )}
                        </Typography>
                      </Box>
                    ) : deployment.last_deployment ? (
                      <Box>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <StatusChip status={deployment.last_deployment.status} size="small" />
                          {deployment.last_deployment.github_run_url && (
                            <Tooltip title="View in GitHub Actions">
                              <IconButton
                                size="small"
                                href={deployment.last_deployment.github_run_url}
                                target="_blank"
                                rel="noopener"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <GitHubIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {formatRelativeTime(deployment.last_deployment.timestamp)}
                          {deployment.last_deployment.triggered_by && (
                            <> by {deployment.last_deployment.triggered_by}</>
                          )}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography color="text.secondary" variant="body2">
                        No recent deployments
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="View Changelog">
                        <IconButton size="small" onClick={() => onChangelog(deployment)}>
                          <DescriptionIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Sync from GitHub">
                        <IconButton size="small" onClick={() => onSync(deployment)}>
                          <SyncIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View History">
                        <IconButton size="small" onClick={() => onViewHistory(deployment)}>
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Rollback">
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => onRollback(deployment)}
                          disabled={isDeploying}
                        >
                          <ReplayIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Deploy">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => onDeploy(deployment)}
                          disabled={isDeploying}
                        >
                          <RocketLaunchIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
                {/* Expanded Details Row */}
                <TableRow key={`${deployment.deployment_id}-details`}>
                  <TableCell colSpan={6} sx={{ py: 0, borderBottom: isExpanded ? 1 : 0 }}>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ py: 2, px: 2, bgcolor: 'grey.50' }}>
                        <Grid container spacing={3}>
                          {/* Components Status */}
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                              Component Health
                            </Typography>
                            {statusInfo?.components && statusInfo.components.length > 0 ? (
                              <List dense sx={{ bgcolor: 'white', borderRadius: 1 }}>
                                {statusInfo.components.map((component, idx) => (
                                  <ListItem key={idx} divider={idx < statusInfo.components!.length - 1}>
                                    <ListItemText
                                      primary={component.name}
                                      secondary={
                                        component.latency_ms
                                          ? `Latency: ${component.latency_ms}ms`
                                          : null
                                      }
                                    />
                                    <StatusChip status={component.status} size="small" />
                                  </ListItem>
                                ))}
                              </List>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No component data available
                              </Typography>
                            )}
                          </Grid>
                          {/* Deployment Info */}
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                              Deployment Details
                            </Typography>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                              <Stack spacing={1}>
                                <Box display="flex" justifyContent="space-between">
                                  <Typography variant="body2" color="text.secondary">
                                    Region
                                  </Typography>
                                  <Typography variant="body2" fontWeight={500}>
                                    {deployment.region}
                                  </Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                  <Typography variant="body2" color="text.secondary">
                                    Domain
                                  </Typography>
                                  <Link
                                    href={`https://${deployment.domain}`}
                                    target="_blank"
                                    rel="noopener"
                                    variant="body2"
                                  >
                                    {deployment.domain}
                                  </Link>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                  <Typography variant="body2" color="text.secondary">
                                    Environment
                                  </Typography>
                                  <Typography variant="body2" fontWeight={500}>
                                    {deployment.environment}
                                  </Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                  <Typography variant="body2" color="text.secondary">
                                    Current Commit
                                  </Typography>
                                  {deployment.commit_sha ? (
                                    <Link
                                      href={`https://github.com/Roboticks-IO/roboticks/commit/${deployment.commit_sha}`}
                                      target="_blank"
                                      rel="noopener"
                                      variant="body2"
                                      sx={{ fontFamily: 'monospace' }}
                                    >
                                      {deployment.commit_sha.substring(0, 7)}
                                    </Link>
                                  ) : (
                                    <Typography variant="body2">-</Typography>
                                  )}
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                  <Typography variant="body2" color="text.secondary">
                                    Last Checked
                                  </Typography>
                                  <Typography variant="body2">
                                    {deployment.last_checked
                                      ? formatRelativeTime(deployment.last_checked)
                                      : '-'}
                                  </Typography>
                                </Box>
                                {statusInfo?.active_incidents_count !== undefined && statusInfo.active_incidents_count > 0 && (
                                  <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" color="text.secondary">
                                      Active Incidents
                                    </Typography>
                                    <Chip
                                      label={statusInfo.active_incidents_count}
                                      color="error"
                                      size="small"
                                    />
                                  </Box>
                                )}
                              </Stack>
                            </Paper>
                          </Grid>
                        </Grid>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// Enhanced History Dialog
function HistoryDialog({
  open,
  onClose,
  deployment,
  history,
  loading,
}: {
  open: boolean;
  onClose: () => void;
  deployment: DeploymentInfo | null;
  history: DeploymentHistoryItem[];
  loading: boolean;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <HistoryIcon />
          Deployment History - {deployment?.label}
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : history.length === 0 ? (
          <Box textAlign="center" py={4}>
            <HistoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography color="text.secondary">No deployment history found.</Typography>
            <Typography variant="caption" color="text.secondary">
              Click sync to fetch history from GitHub Actions.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Commit</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Triggered By</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="body2">
                        {formatRelativeTime(item.timestamp)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(item.timestamp).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {item.commit_sha && item.commit_sha.length >= 7 ? (
                        <Link
                          href={`https://github.com/Roboticks-IO/roboticks/commit/${item.commit_sha}`}
                          target="_blank"
                          rel="noopener"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {item.commit_sha.substring(0, 7)}
                        </Link>
                      ) : (
                        <Typography color="text.secondary">-</Typography>
                      )}
                      {item.head_branch && item.head_branch !== 'main' && (
                        <Chip
                          label={item.head_branch}
                          size="small"
                          variant="outlined"
                          sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={item.status} />
                    </TableCell>
                    <TableCell>
                      {item.duration_seconds ? formatDuration(item.duration_seconds) : '-'}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Avatar sx={{ width: 20, height: 20, fontSize: '0.7rem' }}>
                          {item.triggered_by?.[0]?.toUpperCase() || '?'}
                        </Avatar>
                        <Typography variant="body2">{item.triggered_by}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {item.github_run_url && (
                        <Tooltip title="View in GitHub Actions">
                          <IconButton
                            size="small"
                            href={item.github_run_url}
                            target="_blank"
                            rel="noopener"
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function DeployDialog({
  open,
  onClose,
  deployment,
  commits,
  onDeploy,
}: {
  open: boolean;
  onClose: () => void;
  deployment: DeploymentInfo | null;
  commits: GitHubCommit[];
  onDeploy: (commit?: string, branch?: string) => Promise<void>;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedCommit, setSelectedCommit] = useState<GitHubCommit | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [branch, setBranch] = useState('main');
  const [branches, setBranches] = useState<GitHubBranch[]>([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const isDevDeployment = deployment?.environment === 'development' || deployment?.deployment_id?.includes('dev');

  // Load branches when dialog opens (for dev deployments)
  useEffect(() => {
    if (open && isDevDeployment && branches.length === 0) {
      setBranchesLoading(true);
      fetchBranches()
        .then((data) => setBranches(data.branches))
        .catch((err) => console.error('Failed to load branches:', err))
        .finally(() => setBranchesLoading(false));
    }
  }, [open, isDevDeployment, branches.length]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedCommit(null);
      setInputValue('');
      setBranch('main');
    }
  }, [open]);

  const handleDeploy = async () => {
    setLoading(true);
    try {
      const commitSha = selectedCommit?.sha || (inputValue.length >= 7 ? inputValue : undefined);
      await onDeploy(commitSha, isDevDeployment ? branch : undefined);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  // Filter commits based on input (search by SHA or message)
  const filterOptions = (options: GitHubCommit[], { inputValue }: { inputValue: string }) => {
    const searchTerm = inputValue.toLowerCase();
    if (!searchTerm) return options.slice(0, 30);
    return options.filter(
      (option) =>
        option.sha.toLowerCase().includes(searchTerm) ||
        option.short_sha.toLowerCase().includes(searchTerm) ||
        option.message.toLowerCase().includes(searchTerm) ||
        option.author.name?.toLowerCase().includes(searchTerm) ||
        option.author.login?.toLowerCase().includes(searchTerm)
    ).slice(0, 30);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <RocketLaunchIcon color="primary" />
          Deploy to {deployment?.label}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          This will trigger a deployment to {deployment?.region} ({deployment?.domain}).
        </Alert>

        {/* Branch Selection (only for dev deployments) */}
        {isDevDeployment && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Branch</InputLabel>
            <Select
              value={branch}
              label="Branch"
              onChange={(e) => setBranch(e.target.value)}
              disabled={branchesLoading}
            >
              {branchesLoading ? (
                <MenuItem value="main">Loading branches...</MenuItem>
              ) : (
                branches.map((b) => (
                  <MenuItem key={b.name} value={b.name}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2">{b.name}</Typography>
                      {b.protected && (
                        <Chip label="protected" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        )}

        {/* Commit Autocomplete */}
        <Autocomplete<GitHubCommit, false, false, true>
          options={commits}
          value={selectedCommit}
          onChange={(_, newValue) => {
            if (typeof newValue === 'string') {
              setInputValue(newValue);
              setSelectedCommit(null);
            } else {
              setSelectedCommit(newValue);
            }
          }}
          inputValue={inputValue}
          onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
          filterOptions={filterOptions}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            return `${option.short_sha} - ${option.message.substring(0, 50)}`;
          }}
          isOptionEqualToValue={(option, value) => option.sha === value.sha}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select or search commit"
              placeholder="Search by SHA, message, or author..."
              helperText={
                inputValue && !selectedCommit && inputValue.length >= 7
                  ? `Will deploy commit: ${inputValue.substring(0, 7)}...`
                  : 'Leave empty for latest from selected branch'
              }
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.sha}>
              <Box sx={{ width: '100%' }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
                    {option.short_sha}
                  </Typography>
                  <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                    {option.message.substring(0, 60)}
                    {option.message.length > 60 ? '...' : ''}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  {option.author.avatar_url && (
                    <Avatar src={option.author.avatar_url} sx={{ width: 16, height: 16 }} />
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {option.author.login || option.author.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    • {formatRelativeTime(option.author.date)}
                  </Typography>
                  {option.deployed_to && option.deployed_to.length > 0 && (
                    <Chip
                      label={`Deployed: ${option.deployed_to.join(', ')}`}
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          )}
          sx={{ mb: 2 }}
          freeSolo
        />

        {/* Summary */}
        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" gutterBottom>
            Deployment Summary
          </Typography>
          <Stack spacing={0.5}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Target</Typography>
              <Typography variant="body2" fontWeight={500}>{deployment?.label}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Branch</Typography>
              <Typography variant="body2" fontWeight={500}>{branch}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Commit</Typography>
              <Typography variant="body2" fontFamily="monospace" fontWeight={500}>
                {selectedCommit?.short_sha || (inputValue.length >= 7 ? inputValue.substring(0, 7) : 'Latest')}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleDeploy}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <RocketLaunchIcon />}
        >
          Deploy
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CreateIncidentDialog({
  open,
  onClose,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [title, setTitle] = useState('');
  const [severity, setSeverity] = useState('minor');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await createIncident({
        title: title.trim(),
        severity: severity as 'minor' | 'major' | 'critical',
        message: message.trim() || 'We are investigating this issue.',
        affected_components: [],
      });
      setTitle('');
      setSeverity('minor');
      setMessage('');
      onSubmit();
      onClose();
    } catch (err) {
      setError('Failed to create incident');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ErrorIcon color="error" />
          Create Incident
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          fullWidth
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mt: 1, mb: 2 }}
          placeholder="Brief description of the incident"
        />
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Severity</InputLabel>
          <Select
            value={severity}
            label="Severity"
            onChange={(e) => setSeverity(e.target.value)}
          >
            {SEVERITY_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          fullWidth
          label="Initial Message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          multiline
          rows={3}
          placeholder="Describe what is happening and what you are doing about it"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <AddIcon />}
        >
          Create Incident
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function UpdateIncidentDialog({
  open,
  onClose,
  incident,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  incident: Incident | null;
  onSubmit: () => void;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [status, setStatus] = useState('investigating');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (incident) {
      setStatus(incident.status);
      setMessage('');
    }
  }, [incident]);

  const handleSubmit = async () => {
    if (!incident) return;
    if (!message.trim()) {
      setError('Update message is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await updateIncident(incident.incident_id, {
        status: status as 'investigating' | 'identified' | 'monitoring' | 'resolved',
        message: message.trim(),
      });
      setMessage('');
      onSubmit();
      onClose();
    } catch (err) {
      setError('Failed to update incident');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>Update Incident: {incident?.title}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={status}
            label="Status"
            onChange={(e) => setStatus(e.target.value)}
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          fullWidth
          label="Update Message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          multiline
          rows={3}
          placeholder="Provide an update on the incident status"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <CheckCircleIcon />}
          color={status === 'resolved' ? 'success' : 'primary'}
        >
          {status === 'resolved' ? 'Resolve Incident' : 'Update Incident'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Rollback Confirmation Dialog
function RollbackDialog({
  open,
  onClose,
  deployment,
  onRollback,
}: {
  open: boolean;
  onClose: () => void;
  deployment: DeploymentInfo | null;
  onRollback: () => Promise<void>;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRollback = async () => {
    setLoading(true);
    setError(null);
    try {
      await onRollback();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger rollback');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ReplayIcon color="warning" />
          Rollback Deployment
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Alert severity="warning" sx={{ mb: 2 }}>
          This will roll back <strong>{deployment?.label}</strong> to the previous successful deployment.
        </Alert>
        <Typography variant="body2" color="text.secondary">
          The rollback will deploy the last known working version. This action cannot be undone,
          but you can deploy again after the rollback completes.
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
          <Stack spacing={1}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Region</Typography>
              <Typography variant="body2" fontWeight={500}>{deployment?.region}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Domain</Typography>
              <Typography variant="body2" fontWeight={500}>{deployment?.domain}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Current Commit</Typography>
              <Typography variant="body2" fontFamily="monospace">
                {deployment?.commit_sha?.substring(0, 7) || 'Unknown'}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleRollback}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <ReplayIcon />}
        >
          Rollback
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Changelog Dialog
function ChangelogDialog({
  open,
  onClose,
  deployment,
}: {
  open: boolean;
  onClose: () => void;
  deployment: DeploymentInfo | null;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [changelog, setChangelog] = useState<Changelog | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && deployment?.commit_sha) {
      setLoading(true);
      setError(null);
      fetchChangelog(deployment.commit_sha, 'main')
        .then(setChangelog)
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    }
  }, [open, deployment]);

  const CATEGORY_COLORS: Record<string, string> = {
    Features: 'success',
    'Bug Fixes': 'error',
    Documentation: 'info',
    Refactoring: 'warning',
    Performance: 'secondary',
    Tests: 'default',
    Chores: 'default',
    Other: 'default',
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <DescriptionIcon color="primary" />
          Changelog - {deployment?.label}
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : changelog ? (
          <>
            {changelog.total_commits === 0 ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                This deployment is up to date with main branch.
              </Alert>
            ) : (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {changelog.total_commits} commit{changelog.total_commits > 1 ? 's' : ''} behind main
                  ({changelog.files_changed} files, +{changelog.additions} -{changelog.deletions})
                </Alert>
                <Stack spacing={3}>
                  {Object.entries(changelog.by_category).map(([category, entries]) => (
                    <Box key={category}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Chip
                          label={category}
                          size="small"
                          color={CATEGORY_COLORS[category] as 'success' | 'error' | 'info' | 'warning' | 'secondary' | 'default'}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {entries.length} change{entries.length > 1 ? 's' : ''}
                        </Typography>
                      </Box>
                      <List dense sx={{ bgcolor: 'grey.50', borderRadius: 1 }}>
                        {entries.map((entry) => (
                          <ListItem key={entry.sha} divider>
                            <ListItemAvatar>
                              <Avatar
                                src={entry.author.avatar_url}
                                sx={{ width: 24, height: 24 }}
                              >
                                {entry.author.name?.[0]}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Link
                                  href={entry.html_url}
                                  target="_blank"
                                  rel="noopener"
                                  underline="hover"
                                  color="inherit"
                                >
                                  {entry.title}
                                </Link>
                              }
                              secondary={
                                <Box display="flex" gap={1} alignItems="center">
                                  <Typography variant="caption" fontFamily="monospace">
                                    {entry.short_sha}
                                  </Typography>
                                  <Typography variant="caption">
                                    by {entry.author.login || entry.author.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatRelativeTime(entry.date)}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  ))}
                </Stack>
                {changelog.html_url && (
                  <Box mt={2}>
                    <Link
                      href={changelog.html_url}
                      target="_blank"
                      rel="noopener"
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <GitHubIcon fontSize="small" />
                      View full diff on GitHub
                      <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </Link>
                  </Box>
                )}
              </>
            )}
          </>
        ) : (
          <Typography color="text.secondary">No changelog available.</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// Deployment Logs Dialog
function LogsDialog({
  open,
  onClose,
  run,
}: {
  open: boolean;
  onClose: () => void;
  run: WorkflowRun | null;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [jobs, setJobs] = useState<WorkflowJob[]>([]);
  const [selectedJob, setSelectedJob] = useState<WorkflowJob | null>(null);
  const [logs, setLogs] = useState<WorkflowLogsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh] = useState(true);

  // Load jobs when dialog opens or run changes
  useEffect(() => {
    if (open && run) {
      loadJobs();
    }
  }, [open, run?.id]);

  // Auto-refresh for in-progress runs
  useEffect(() => {
    if (!open || !run || !autoRefresh) return;
    if (run.status !== 'in_progress' && run.status !== 'queued') return;

    const interval = setInterval(() => {
      loadJobs();
    }, 5000);

    return () => clearInterval(interval);
  }, [open, run, autoRefresh]);

  const loadJobs = async () => {
    if (!run) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetchWorkflowJobs(run.id);
      setJobs(response.jobs);
      // Auto-select the first job or currently running job
      if (!selectedJob || !response.jobs.find(j => j.id === selectedJob.id)) {
        const runningJob = response.jobs.find(j => j.status === 'in_progress');
        setSelectedJob(runningJob || response.jobs[0] || null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async (jobId: number) => {
    if (!run) return;
    setLogsLoading(true);
    try {
      const response = await fetchWorkflowLogs(run.id, jobId);
      setLogs(response);
    } catch (err) {
      setLogs(null);
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedJob && run?.status === 'completed') {
      loadLogs(selectedJob.id);
    }
  }, [selectedJob, run?.status]);

  const getStepIcon = (step: WorkflowJob['steps'][0]) => {
    if (step.status === 'in_progress') return <CircularProgress size={14} />;
    if (step.conclusion === 'success') return <CheckCircleIcon fontSize="small" color="success" />;
    if (step.conclusion === 'failure') return <ErrorIcon fontSize="small" color="error" />;
    if (step.conclusion === 'skipped') return <WarningIcon fontSize="small" color="disabled" />;
    return <CircularProgress size={14} variant="determinate" value={0} />;
  };

  const isRunComplete = run?.status === 'completed';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <TerminalIcon color="primary" />
            Deployment Logs
            {run && (
              <Chip
                label={run.conclusion || run.status}
                size="small"
                color={
                  run.conclusion === 'success' ? 'success' :
                  run.conclusion === 'failure' ? 'error' :
                  run.status === 'in_progress' ? 'warning' : 'default'
                }
              />
            )}
          </Box>
          {run?.html_url && (
            <Tooltip title="View in GitHub">
              <IconButton
                size="small"
                href={run.html_url}
                target="_blank"
                rel="noopener"
              >
                <GitHubIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {loading && jobs.length === 0 ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
        ) : (
          <Grid container sx={{ height: { xs: 'auto', md: 500 }, flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Jobs sidebar */}
            <Grid item xs={12} md={3} sx={{ borderRight: { md: 1 }, borderBottom: { xs: 1, md: 0 }, borderColor: 'divider', height: { xs: 'auto', md: '100%' }, maxHeight: { xs: 200, md: '100%' }, overflow: 'auto' }}>
              <List dense sx={{ py: 0 }}>
                {jobs.map((job) => (
                  <ListItem
                    key={job.id}
                    button
                    selected={selectedJob?.id === job.id}
                    onClick={() => setSelectedJob(job)}
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          {job.status === 'in_progress' ? (
                            <CircularProgress size={14} />
                          ) : job.conclusion === 'success' ? (
                            <CheckCircleIcon fontSize="small" color="success" />
                          ) : job.conclusion === 'failure' ? (
                            <ErrorIcon fontSize="small" color="error" />
                          ) : (
                            <CircularProgress size={14} variant="determinate" value={0} />
                          )}
                          <Typography variant="body2" fontWeight={selectedJob?.id === job.id ? 600 : 400}>
                            {job.name}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        job.duration_seconds ? formatDuration(job.duration_seconds) :
                        job.status === 'in_progress' ? 'Running...' : 'Pending'
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>

            {/* Steps and logs area */}
            <Grid item xs={12} md={9} sx={{ height: { xs: 'auto', md: '100%' }, minHeight: { xs: 300, md: 0 }, overflow: 'auto', bgcolor: 'grey.900' }}>
              {selectedJob ? (
                <Box>
                  {/* Steps */}
                  <Box sx={{ bgcolor: 'grey.800', p: 1 }}>
                    <Typography variant="caption" color="grey.400" sx={{ px: 1 }}>
                      Steps
                    </Typography>
                    <List dense sx={{ py: 0 }}>
                      {selectedJob.steps.map((step) => (
                        <ListItem key={step.number} sx={{ py: 0.5 }}>
                          <Box display="flex" alignItems="center" gap={1} width="100%">
                            {getStepIcon(step)}
                            <Typography
                              variant="body2"
                              color={step.conclusion === 'failure' ? 'error.light' : 'grey.300'}
                              sx={{ flex: 1 }}
                            >
                              {step.name}
                            </Typography>
                            {step.started_at && step.completed_at && (
                              <Typography variant="caption" color="grey.500">
                                {formatDuration(
                                  Math.round(
                                    (new Date(step.completed_at).getTime() -
                                      new Date(step.started_at).getTime()) /
                                      1000
                                  )
                                )}
                              </Typography>
                            )}
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  {/* Logs */}
                  {isRunComplete && (
                    <Box sx={{ p: 2 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="caption" color="grey.400">
                          Logs
                        </Typography>
                        {logsLoading && <CircularProgress size={14} />}
                      </Box>
                      {logs?.logs ? (
                        <Box
                          component="pre"
                          sx={{
                            bgcolor: 'grey.950',
                            color: 'grey.300',
                            p: 2,
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            fontFamily: 'monospace',
                            overflow: 'auto',
                            maxHeight: 300,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                          }}
                        >
                          {logs.logs}
                        </Box>
                      ) : logs?.logs_by_job && selectedJob ? (
                        <Box
                          component="pre"
                          sx={{
                            bgcolor: 'grey.950',
                            color: 'grey.300',
                            p: 2,
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            fontFamily: 'monospace',
                            overflow: 'auto',
                            maxHeight: 300,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                          }}
                        >
                          {Object.entries(logs.logs_by_job).find(([name]) =>
                            name.includes(selectedJob.name) || selectedJob.name.includes(name)
                          )?.[1] || 'No logs available for this job'}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="grey.500">
                          {logsLoading ? 'Loading logs...' : 'Logs not available yet'}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {!isRunComplete && (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="body2" color="grey.400">
                        Logs will be available when the job completes
                      </Typography>
                      <Box display="flex" alignItems="center" justifyContent="center" gap={1} mt={1}>
                        <CircularProgress size={14} />
                        <Typography variant="caption" color="grey.500">
                          Auto-refreshing every 5 seconds
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography color="grey.500">Select a job to view details</Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function OpsPage() {
  const { user, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeTab, setActiveTab] = useState(0);
  const [deployments, setDeployments] = useState<DeploymentInfo[]>([]);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [workflowRuns, setWorkflowRuns] = useState<WorkflowRun[]>([]);
  const [commits, setCommits] = useState<GitHubCommit[]>([]);
  const [statusData, setStatusData] = useState<StatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  // Dialog states
  const [deployDialogOpen, setDeployDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [rollbackDialogOpen, setRollbackDialogOpen] = useState(false);
  const [changelogDialogOpen, setChangelogDialogOpen] = useState(false);
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [selectedDeployment, setSelectedDeployment] = useState<DeploymentInfo | null>(null);
  const [selectedRun, setSelectedRun] = useState<WorkflowRun | null>(null);
  const [history, setHistory] = useState<DeploymentHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [createIncidentOpen, setCreateIncidentOpen] = useState(false);
  const [updateIncidentOpen, setUpdateIncidentOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);

  const loadDeployments = async () => {
    try {
      const data = await fetchDeployments();
      setDeployments(data);
    } catch (err) {
      console.error('Failed to load deployments:', err);
    }
  };

  const loadIncidents = async () => {
    try {
      const data = await fetchActiveIncidents();
      setIncidents(data);
    } catch (err) {
      console.error('Failed to load incidents:', err);
    }
  };

  const loadWorkflowRuns = async () => {
    try {
      const data = await fetchWorkflowRuns();
      setWorkflowRuns(data.runs);
    } catch (err) {
      console.error('Failed to load workflow runs:', err);
    }
  };

  const loadCommits = async () => {
    try {
      const data = await fetchCommits({ perPage: 30 });
      setCommits(data.commits);
    } catch (err) {
      console.error('Failed to load commits:', err);
    }
  };

  const loadStatusData = async () => {
    try {
      const data = await fetchStatus();
      setStatusData(data);
    } catch (err) {
      console.error('Failed to load status data:', err);
    }
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([loadDeployments(), loadIncidents(), loadWorkflowRuns(), loadCommits(), loadStatusData()]);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Initial load with automatic sync from GitHub
    const initialLoad = async () => {
      // Sync deployment history from GitHub in background (don't block initial load)
      syncAllDeployments().catch((err) => console.error('Background sync failed:', err));
      await loadAll();
    };
    initialLoad();

    // Refresh every 30 seconds (without syncing to avoid rate limits)
    const interval = setInterval(loadAll, 30000);
    return () => clearInterval(interval);
  }, [loadAll]);

  const handleOpenDeploy = (deployment: DeploymentInfo) => {
    setSelectedDeployment(deployment);
    setDeployDialogOpen(true);
  };

  const handleDeploy = async (commit?: string, branch?: string) => {
    if (!selectedDeployment) return;
    await triggerDeploy(selectedDeployment.deployment_id, commit, branch);
    await loadAll();
  };

  const handleOpenHistory = async (deployment: DeploymentInfo) => {
    setSelectedDeployment(deployment);
    setHistoryDialogOpen(true);
    setHistoryLoading(true);
    try {
      const historyData = await fetchDeploymentHistory(deployment.deployment_id, 50);
      setHistory(historyData);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSync = async (deployment: DeploymentInfo) => {
    setSyncing(true);
    try {
      await syncDeploymentHistory(deployment.deployment_id);
      await loadAll();
    } catch (err) {
      console.error('Failed to sync:', err);
    } finally {
      setSyncing(false);
    }
  };

  const handleOpenRollback = (deployment: DeploymentInfo) => {
    setSelectedDeployment(deployment);
    setRollbackDialogOpen(true);
  };

  const handleRollback = async () => {
    if (!selectedDeployment) return;
    await triggerRollback(selectedDeployment.deployment_id);
    await loadAll();
  };

  const handleOpenChangelog = (deployment: DeploymentInfo) => {
    setSelectedDeployment(deployment);
    setChangelogDialogOpen(true);
  };

  const handleOpenLogs = (run: WorkflowRun) => {
    setSelectedRun(run);
    setLogsDialogOpen(true);
  };

  const handleOpenUpdateIncident = (incident: Incident) => {
    setSelectedIncident(incident);
    setUpdateIncidentOpen(true);
  };

  if (loading && deployments.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: { xs: 1.5, md: 2 },
          borderBottom: '4px solid',
          borderColor: 'primary.dark',
        }}
      >
        <Container maxWidth="xl">
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={{ xs: 1, md: 0 }}>
            <Box display="flex" alignItems="center" gap={{ xs: 1, md: 2 }}>
              <img
                src="/roboticks-icon.svg"
                alt="Roboticks"
                style={{ height: 32, width: 'auto' }}
              />
              <Box>
                <Typography variant="h6" fontWeight={700} fontSize={{ xs: '1rem', md: '1.25rem' }}>
                  Roboticks Ops
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8, display: { xs: 'none', sm: 'block' } }}>
                  Deployment & Incident Management
                </Typography>
              </Box>
            </Box>
            <Stack direction="row" spacing={{ xs: 0.5, md: 2 }} alignItems="center">
              <Typography variant="body2" sx={{ opacity: 0.8, display: { xs: 'none', md: 'block' } }}>
                {user?.email}
              </Typography>
              <Tooltip title="Refresh">
                <IconButton onClick={loadAll} sx={{ color: 'white' }} disabled={loading} size="small">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="outlined"
                size="small"
                onClick={logout}
                sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', fontSize: { xs: '0.75rem', md: '0.875rem' }, px: { xs: 1, md: 2 } }}
              >
                Sign Out
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 }, px: { xs: 2, md: 3 } }}>
        {error && (
          <Alert severity="error" sx={{ mb: { xs: 2, md: 3 } }}>
            {error}
          </Alert>
        )}

        {syncing && (
          <Alert severity="info" sx={{ mb: { xs: 2, md: 3 } }}>
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={16} />
              Syncing deployment history from GitHub...
            </Box>
          </Alert>
        )}

        {/* Active Deployments Banner */}
        <ActiveDeploymentsBanner runs={workflowRuns} onViewLogs={handleOpenLogs} />

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ mb: { xs: 2, md: 3 }, bgcolor: 'white', borderRadius: 1, px: { xs: 1, md: 2 } }}
          variant={isMobile ? 'fullWidth' : 'standard'}
        >
          <Tab label="Deployments" sx={{ fontSize: { xs: '0.875rem', md: '0.9375rem' } }} />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontSize={{ xs: '0.875rem', md: '0.9375rem' }}>
                  Incidents
                </Typography>
                {incidents.filter((i) => i.status !== 'resolved').length > 0 && (
                  <Chip
                    label={incidents.filter((i) => i.status !== 'resolved').length}
                    color="error"
                    size="small"
                  />
                )}
              </Box>
            }
          />
        </Tabs>

        {/* Deployments Tab */}
        {activeTab === 0 && (
          <>
            {/* Deployments Table */}
            <EnhancedDeploymentTable
              deployments={deployments}
              workflowRuns={workflowRuns}
              statusData={statusData}
              onDeploy={handleOpenDeploy}
              onViewHistory={handleOpenHistory}
              onSync={handleSync}
              onRollback={handleOpenRollback}
              onChangelog={handleOpenChangelog}
            />

            {/* Recent Commits */}
            <Box sx={{ mt: 3 }}>
              <RecentCommitsList commits={commits} deployments={deployments} />
            </Box>
          </>
        )}

        {/* Incidents Tab */}
        {activeTab === 1 && (
          <Paper sx={{ mb: { xs: 2, md: 4 } }}>
            <Box
              p={{ xs: 1.5, md: 2 }}
              borderBottom={1}
              borderColor="divider"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={1}
            >
              <Typography variant="h6" fontSize={{ xs: '1rem', md: '1.25rem' }}>Active Incidents</Typography>
              <Button
                variant="contained"
                color="error"
                startIcon={!isMobile && <AddIcon />}
                onClick={() => setCreateIncidentOpen(true)}
                size={isMobile ? 'small' : 'medium'}
              >
                {isMobile ? 'Create' : 'Create Incident'}
              </Button>
            </Box>
            {incidents.length === 0 ? (
              <Box p={4} textAlign="center">
                <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                <Typography color="text.secondary">
                  No active incidents. All systems operational.
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: { xs: 600, md: 'auto' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, minWidth: { xs: 150, md: 'auto' } }}>Incident</TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: { xs: 100, md: 'auto' } }}>Severity</TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: { xs: 100, md: 'auto' } }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: { xs: 100, md: 'auto' } }}>Created</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, minWidth: { xs: 80, md: 'auto' } }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {incidents.map((incident) => (
                      <TableRow key={incident.incident_id} hover>
                        <TableCell>
                          <Typography fontWeight={500} fontSize={{ xs: '0.875rem', md: '1rem' }}>{incident.title}</Typography>
                        </TableCell>
                        <TableCell>
                          <StatusChip status={incident.severity} />
                        </TableCell>
                        <TableCell>
                          <StatusChip status={incident.status} />
                        </TableCell>
                        <TableCell>
                          <Typography fontSize={{ xs: '0.875rem', md: '1rem' }}>
                            {formatRelativeTime(incident.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Update Incident">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenUpdateIncident(incident)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}
      </Container>

      {/* Dialogs */}
      <DeployDialog
        open={deployDialogOpen}
        onClose={() => setDeployDialogOpen(false)}
        deployment={selectedDeployment}
        commits={commits}
        onDeploy={handleDeploy}
      />
      <HistoryDialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        deployment={selectedDeployment}
        history={history}
        loading={historyLoading}
      />
      <CreateIncidentDialog
        open={createIncidentOpen}
        onClose={() => setCreateIncidentOpen(false)}
        onSubmit={loadIncidents}
      />
      <UpdateIncidentDialog
        open={updateIncidentOpen}
        onClose={() => setUpdateIncidentOpen(false)}
        incident={selectedIncident}
        onSubmit={loadIncidents}
      />
      <RollbackDialog
        open={rollbackDialogOpen}
        onClose={() => setRollbackDialogOpen(false)}
        deployment={selectedDeployment}
        onRollback={handleRollback}
      />
      <ChangelogDialog
        open={changelogDialogOpen}
        onClose={() => setChangelogDialogOpen(false)}
        deployment={selectedDeployment}
      />
      <LogsDialog
        open={logsDialogOpen}
        onClose={() => setLogsDialogOpen(false)}
        run={selectedRun}
      />
    </Box>
  );
}
